import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useBranding } from "../lib/branding";
import { BRAND } from "../config/brand";
import { SignOut, Buildings, List, X, CaretDown } from "@phosphor-icons/react";
import AgencySwitcher from "./AgencySwitcher";
import { ShiftWidget } from "./ShiftWidget";

const NAV = [
    {
        type: "group",
        label: "Operatività",
        items: [
            { to: "/dashboard", label: "Dashboard", desc: "Quadratura e KPI" },
            { to: "/transactions", label: "Transazioni", desc: "Movimenti del periodo" },
            { to: "/closures", label: "Chiusure", desc: "Chiusura mese e storico", roles: ["owner", "agency_manager"] },
        ],
    },
    {
        type: "group",
        label: "Anagrafiche",
        items: [
            { to: "/providers", label: "Fornitori", desc: "Fidi operatori scommesse" },
            { to: "/clients", label: "Clienti", desc: "Rubrica e fidi personali" },
            { to: "/categories", label: "Categorie", desc: "Tipologie di spesa", roles: ["owner", "agency_manager"] },
        ],
    },
    {
        type: "group",
        label: "Team",
        items: [
            { to: "/team", label: "Performance & turni", desc: "Operatori, schedulazione, KPI", roles: ["owner", "agency_manager"] },
            { to: "/settings/users", label: "Utenti", desc: "Crea operatori e manager", roles: ["owner", "agency_manager"] },
        ],
    },
    {
        type: "group",
        label: "Finanze",
        items: [
            { to: "/wallets", label: "Wallet", desc: "Cassa, banca, utili", roles: ["owner", "agency_manager"] },
            { to: "/reports", label: "Report", desc: "Andamento P&L mensile", roles: ["owner", "agency_manager"] },
        ],
    },
    {
        type: "group",
        label: "Impostazioni",
        items: [
            { to: "/settings/company", label: "Azienda", desc: "Brand, logo, accent (owner)", roles: ["owner"] },
            { to: "/settings/agency", label: "Agenzia", desc: "Dati della sede corrente", roles: ["owner", "agency_manager"] },
            { to: "/settings/avanzate", label: "Avanzate", desc: "Apertura saldi e funzioni di onboarding (owner)", roles: ["owner"] },
            { to: "/audit-log", label: "Cronologia", desc: "Chi ha fatto cosa, ultime 72h", roles: ["owner", "agency_manager"] },
            { to: "/settings/profile", label: "Profilo", desc: "Il tuo nome e account" },
        ],
    },
];


function SidebarNav({ userRole, onItemClick }) {
    const location = useLocation();
    const [openGroups, setOpenGroups] = useState(() => {
        // Auto-expand the group containing the current route
        const init = {};
        NAV.forEach((n) => {
            if (n.type === "group") {
                const match = n.items.some((i) => location.pathname.startsWith(i.to));
                init[n.label] = match;
            }
        });
        return init;
    });

    const toggle = (label) => setOpenGroups((g) => ({ ...g, [label]: !g[label] }));

    return (
        <nav className="flex-1 overflow-y-auto py-4" data-testid="sidebar-nav">
            {NAV.map((n) => {
                if (n.type === "link") {
                    return (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            onClick={onItemClick}
                            className={({ isActive }) =>
                                `flex items-center px-6 py-2.5 text-sm font-bold border-l-2 transition-colors ${
                                    isActive
                                        ? "border-signal text-ink bg-zinc-50"
                                        : "border-transparent text-zinc-600 hover:text-ink hover:bg-zinc-50"
                                }`
                            }
                            data-testid={`sidenav-${n.label.toLowerCase()}`}
                        >
                            {n.label}
                        </NavLink>
                    );
                }
                const visibleItems = n.items.filter(
                    (i) => !i.roles || i.roles.includes(userRole),
                );
                if (visibleItems.length === 0) return null;
                const isOpen = !!openGroups[n.label];
                const groupActive = visibleItems.some((i) => location.pathname.startsWith(i.to));
                return (
                    <div key={n.label} className="mb-1">
                        <button
                            onClick={() => toggle(n.label)}
                            className={`w-full flex items-center justify-between px-6 py-2.5 text-sm font-bold border-l-2 transition-colors ${
                                groupActive
                                    ? "border-signal text-ink"
                                    : "border-transparent text-zinc-600 hover:text-ink"
                            }`}
                            data-testid={`sidenav-group-${n.label.toLowerCase()}`}
                        >
                            <span>{n.label}</span>
                            <CaretDown
                                size={10}
                                weight="bold"
                                className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
                            />
                        </button>
                        {isOpen && (
                            <div className="bg-zinc-50">
                                {visibleItems.map((i) => (
                                    <NavLink
                                        key={i.to}
                                        to={i.to}
                                        onClick={onItemClick}
                                        className={({ isActive }) =>
                                            `block pl-10 pr-6 py-2 text-xs transition-colors ${
                                                isActive
                                                    ? "text-ink font-bold bg-white"
                                                    : "text-zinc-600 hover:text-ink hover:bg-white"
                                            }`
                                        }
                                        data-testid={`sidenav-${i.label.toLowerCase()}`}
                                    >
                                        {i.label}
                                    </NavLink>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </nav>
    );
}


export default function AppShell({ children }) {
    const { user, agencyId, logout, currentShift } = useAuth();
    const { company, logo_b64 } = useBranding();
    const navigate = useNavigate();
    const [brandLogoOk, setBrandLogoOk] = useState(true);
    const [activeBrand, setActiveBrand] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer

    useEffect(() => {
        let cancelled = false;
        if (!agencyId) {
            setActiveBrand(null);
            return;
        }
        import("../lib/api").then(({ api }) => {
            api.get("/agencies")
                .then((r) => {
                    if (cancelled) return;
                    const ag = r.data.find((a) => a.id === agencyId);
                    if (ag?.brand_concessionario) {
                        import("../config/concessionari").then(({ findConcessionario }) => {
                            if (!cancelled) setActiveBrand(findConcessionario(ag.brand_concessionario));
                        });
                    } else {
                        setActiveBrand(null);
                    }
                })
                .catch(() => {});
        });
        return () => { cancelled = true; };
    }, [agencyId]);

    const tenantName = company?.name || "";
    const operatorBlocked = user?.role === "operator" && !currentShift;

    return (
        <div
            className="min-h-screen bg-[#fafafa]"
            style={activeBrand ? { "--brand-accent": activeBrand.color } : undefined}
        >
            {activeBrand && (
                <div
                    className="h-1"
                    style={{ background: activeBrand.color }}
                    data-testid="brand-accent-stripe"
                />
            )}

            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-50 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar — fixed on desktop, drawer on mobile */}
            <aside
                className={`fixed top-0 left-0 h-screen w-64 bg-white border-r border-zinc-200 z-50 flex flex-col transform transition-transform duration-200 lg:translate-x-0 ${
                    sidebarOpen ? "translate-x-0" : "-translate-x-full"
                }`}
                data-testid="app-sidebar"
            >
                <div className="px-6 py-5 flex items-center justify-between border-b border-zinc-200">
                    <Link
                        to="/dashboard"
                        className="flex items-center gap-3"
                        onClick={() => setSidebarOpen(false)}
                        data-testid="sidebar-logo"
                    >
                        {brandLogoOk && BRAND.glyph ? (
                            <img
                                src={BRAND.glyph}
                                alt={BRAND.name}
                                className="w-10 h-10 object-contain"
                                onError={() => setBrandLogoOk(false)}
                                data-testid="platform-logo"
                            />
                        ) : (
                            <div className="w-10 h-10 border border-ink flex items-center justify-center">
                                <div className="w-3 h-3 bg-signal" />
                            </div>
                        )}
                        <div>
                            <div className="text-base font-black tracking-tighter leading-none" data-testid="app-brand-name">
                                {BRAND.name}
                            </div>
                            <div className="overline leading-none mt-1">{BRAND.tagline}</div>
                        </div>
                    </Link>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="lg:hidden text-zinc-400 hover:text-ink"
                        aria-label="Chiudi menu"
                        data-testid="sidebar-close"
                    >
                        <X size={20} weight="bold" />
                    </button>
                </div>
                <SidebarNav userRole={user?.role} onItemClick={() => setSidebarOpen(false)} />
                {user && (
                    <div className="border-t border-zinc-200 px-6 py-4" data-testid="sidebar-user-info">
                        <div className="text-sm font-bold text-ink truncate" title={user.name || user.email}>
                            {user.name || "—"}
                        </div>
                        <div className="overline mt-0.5 truncate" title={user.email}>
                            {user.role} · {user.email}
                        </div>
                    </div>
                )}
                <div className="border-t border-zinc-200 px-6 py-3 text-xs text-zinc-500 flex items-center gap-2">
                    <Buildings size={12} weight="bold" />
                    <span>v1.0</span>
                </div>
            </aside>

            {/* Main column */}
            <div className="lg:pl-64">
                <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-zinc-200">
                    <div className="px-4 md:px-6 py-3 md:py-5 flex items-center gap-3">
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="lg:hidden text-zinc-600 hover:text-ink p-1"
                            aria-label="Apri menu"
                            data-testid="sidebar-toggle"
                        >
                            <List size={22} weight="bold" />
                        </button>

                        {/* Co-brand: company name + logo (centered, wraps if long) */}
                        {(logo_b64 || tenantName) && (
                            <div
                                className="hidden md:flex items-center gap-2 max-w-[260px]"
                                data-testid="co-brand"
                                title={tenantName || ""}
                            >
                                {logo_b64 && (
                                    <img
                                        src={logo_b64}
                                        alt="Logo azienda"
                                        className="w-8 h-8 object-contain flex-shrink-0"
                                    />
                                )}
                                {tenantName && (
                                    <div
                                        className="text-sm font-black text-ink tracking-tighter leading-tight text-center break-words flex-1"
                                        style={{
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                        }}
                                    >
                                        {tenantName}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Spacer pushes the rest to the right */}
                        <div className="flex-1" />

                        <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
                            <ShiftWidget />
                            <AgencySwitcher />
                            <div className="hidden xl:flex flex-col items-end max-w-[140px]">
                                <div className="text-sm font-bold truncate w-full text-right" title={user?.name || ""}>
                                    {user?.name}
                                </div>
                                <div className="overline">{user?.role}</div>
                            </div>
                            <button
                                onClick={async () => {
                                    await logout();
                                    navigate("/login");
                                }}
                                className="btn-secondary !py-2 !px-3 flex items-center gap-2 flex-shrink-0"
                                data-testid="logout-button"
                                aria-label="Esci"
                            >
                                <SignOut size={16} weight="bold" />
                                <span className="hidden sm:inline">Esci</span>
                            </button>
                        </div>
                    </div>
                </header>

                <main className="px-4 md:px-8 lg:px-12 py-8 max-w-[1600px] mx-auto">
                    {operatorBlocked ? (
                        <div className="max-w-xl mx-auto text-center py-16">
                            <div className="overline mb-3 text-signal">Turno chiuso</div>
                            <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-4">
                                Apri un turno per iniziare.
                            </h1>
                            <p className="text-sm text-zinc-600 mb-8 leading-relaxed">
                                Come operatore devi aprire un turno prima di lavorare.
                                Verrà fotografata la cassa di inizio turno e tutte le tue
                                transazioni saranno tracciate. A fine turno farai la
                                riconciliazione POS, conta cassa e logout automatico.
                            </p>
                            <div className="text-xs text-zinc-500">
                                Usa il pulsante <strong>Apri turno</strong> in alto a destra
                                dell&apos;header per iniziare.
                            </div>
                        </div>
                    ) : (
                        // key={agencyId} forza il remount di ogni pagina figlia quando
                        // l'utente cambia agenzia dal dropdown. Garantisce che ogni
                        // useEffect di fetch riparta con la nuova agency, senza dover
                        // ricordarsi di mettere agencyId nelle deps di ogni singola
                        // sub-page (fix bug "chiusure non si aggiornano al switch").
                        <div key={agencyId || "no-agency"} data-testid="agency-scoped-content">
                            {children}
                        </div>
                    )}
                </main>

                <footer className="border-t border-zinc-200 mt-16 px-6 py-6 flex items-center justify-between text-xs text-zinc-500">
                    <div className="flex items-center gap-2">
                        <Buildings size={14} weight="bold" />
                        Powered by <strong className="text-zinc-700">{BRAND.name}</strong> · Controllo finanziario per agenzie
                    </div>
                    <div className="overline">v1.0</div>
                </footer>
            </div>
        </div>
    );
}
